<template>
  <div class="flex flex-row flex-wrap w-9">
    <HeadTitle
      title="Detalle de cartas externas"
      description="Detalle de cartas externas precargadas en el sistema"
    />
    <EditRecordButton @update:is-edit-mode="toggleEditMode" />
  </div>
  <Divider />
  <div v-if="isLoading" class="grid w-10 mt-4">
    <div class="col-12">
      <Skeleton width="10rem" height="1.5rem" class="mb-3"></Skeleton>
    </div>
    <div class="flex flex-column md:col-6">
      <Skeleton width="5rem" height="1rem" class="mb-2"></Skeleton>
      <Skeleton width="100%" height="2.5rem"></Skeleton>
    </div>
    <div class="flex flex-column md:col-6">
      <Skeleton width="5rem" height="1rem" class="mb-2"></Skeleton>
      <Skeleton width="100%" height="2.5rem"></Skeleton>
    </div>
    <div class="col-12 mt-4">
      <Skeleton width="10rem" height="1.5rem" class="mb-3"></Skeleton>
      <Skeleton width="100%" height="400px"></Skeleton>
    </div>
  </div>
  <div v-else>
    <div
      class="flex flex-row flex-wrap w-2 min-h-2 justify-content-between align-items-center mb-3"
    >
      <h3 class="font-normal">Configuración básica</h3>
    </div>
    <div class="formgrid grid w-12 md:w-6 mt-2">
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="name">Nombre <span class="text-red-600">*</span></label>
        <InputText
          v-model="name"
          id="name"
          type="text"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          nameError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="description"
          >Descripción <span class="text-red-600">*</span></label
        >
        <InputText
          id="description"
          v-model="description"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          descriptionError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-8 flex flex-column">
        <div class="flex flex-row mb-2">
          <label for="approver">Correos a notificar</label>
          <InfoButton
            message="Por cada archivo cargado se enviara un correo con copia del archivo a los emails seleccionados."
          />
        </div>
        <Chips
          v-model="emailsToNotify"
          separator=","
          invalid
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          emailsError || '&nbsp;'
        }}</small>
      </div>
    </div>
    <div class="flex flex-row flex-wrap w-full min-h-2 align-items-center">
      <h3 class="font-normal">Cartas precargadas</h3>
      <Button
        icon="pi pi-plus"
        severity="secondary"
        v-tooltip.bottom="'Agregar nómina(s)'"
        rounded
        aria-label="Plus"
        :disabled="!isEditMode"
        @click="modalVisible = true"
        class="ml-3"
      />
    </div>
    <div class="flex flex-column w-6">
      <DataTable
        class="tabla mb-5"
        :value="lettersRequests.data"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        style="width: 100%"
        v-model:filters="mainFilters"
        :globalFilterFields="['employee.code', 'employee.name']"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        :totalRecords="lettersRequests.totalItems"
        :currentPageReportTemplate="currentPageReportTemplate"
        scrollable
        scrollHeight="600px"
      >
        <template #empty> No hay archivos a validar.</template>
        <template #header>
          <div class="flex justify-content-end">
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText
                v-model="mainFilters['global'].value"
                placeholder="Buscar"
              />
            </span>
          </div>
        </template>
        <template></template>
        <Column field="employee" header="Empleado">
          <template #body="{ data, field }">
            <span v-if="data[field]"
              >{{ data.employee.code }} - {{ data.employee.name }}</span
            >
          </template>
        </Column>
        <Column header="Acciones">
          <template #body="{ data, field }">
            <Button
              icon="pi pi-download"
              class="p-button-rounded p-button-primary p-button-text text-sm font-light"
              @click="downloadFile(data.url)"
            />
            <ConfirmPopup></ConfirmPopup>
            <Button
              icon="pi pi-trash"
              class="p-button-sm btn-delete"
              severity="danger"
              text
              rounded
              aria-label="Cancel"
              v-if="isEditMode"
              @click="deleteLetter($event, data['id'])"
            />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
  <Footer>
    <template #left>
      <AuditButton
        :entities="['LetterCertifConfig', 'LetterCertifRequest']"
        :record-ids="recordsIds"
        :root-id="letterCertifConfigId"
      />
    </template>
    <Button
      label="Cancelar"
      class="p-button-secondary text-sm font-light mr-2"
      @click="router.back()"
    />
    <Button
      label="Guardar"
      class="p-button-primary text-sm font-light"
      :disabled="!isValidToSave"
      :loading="saving"
      @click="onSave"
    />
  </Footer>
  <Dialog
    v-model:visible="modalVisible"
    modal
    header="Agregar nuevas cartas"
    :style="{ width: '50vw' }"
    :breakpoints="{ '960px': '75vw', '641px': '100vw' }"
    :onHide="onCloseDialog"
  >
    <div class="flex flex-row">
      <FileUpload
        mode="basic"
        accept=".zip"
        chooseLabel="cargar archivo"
        class="text-sm font-light"
        custom-upload
        @uploader="removeFile"
        @select="saveFile($event)"
      />
      <InfoButton
        message="Solo se permite subir un archivo comprimido de tipo .zip, que contenga archivos nombrados con el siguiente formato: <Codigo de nomina>_<numero de identificación>.pdf.
                    Segun el formato de los archivos, se crearan las cartas asociadas a los empleados."
        class="ml-2"
      />
    </div>
    <div class="flex flex-column w-10">
      <DataTable
        class="tabla mb-5"
        :value="mappedExternalFiles.data"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        style="width: 100%"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        :totalRecords="mappedExternalFiles.totalItems"
        :currentPageReportTemplate="currentPageReportTemplate"
        v-model:selection="selectedRecordsToRemove"
        v-model:filters="filters"
        :globalFilterFields="['fileName', 'employee.code', 'employee.name']"
        scrollable
        scrollHeight="600px"
        :loading="filesAreLoadig"
      >
        <template #empty> No hay archivos a validar.</template>
        <template #header>
          <div class="flex justify-content-end">
            <Button
              icon="pi pi-trash"
              label="Remover Seleción"
              @click="onRemoveRecords($event)"
              severity="secondary"
              class="text-sm font-light mr-3"
            />
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText
                v-model="filters['global'].value"
                placeholder="Buscar"
              />
            </span>
          </div>
        </template>
        <Column selectionMode="multiple" headerStyle="width: 3rem"> </Column>
        <Column header="Nombre del archivo">
          <template #body="{ data }">
            <span>{{ data.fileName }}</span>
          </template>
        </Column>
        <Column field="employee" header="Empleado">
          <template #body="{ data, field }">
            <span v-if="data[field]"
              >{{ data[field].code }} - {{ data[field].name }}</span
            >
          </template>
        </Column>
        <Column field="error" header="Error">
          <template #body="{ data, field }">
            <span v-if="data[field]" class="text-red-400 text-sm">{{
              data[field]
            }}</span>
          </template>
        </Column>
        <Column header="Acciones">
          <template #body="{ data }">
            <div class="actions">
              <Button
                v-tooltip.bottom="'Ver archivo'"
                class="action-btn view p-button-sm mr-2"
                icon="pi pi-eye"
                @click="downloadFile(data.url)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
    <template #footer>
      <Button
        label="Cancelar"
        class="text-sm font-ligth"
        icon="pi pi-times"
        @click="modalVisible = false"
        text
      />
      <Button
        label="Agregar"
        class="text-sm font-ligth"
        icon="pi pi-check"
        @click="addExternalLetters"
        autofocus
        :disabled="!isValidToAdd"
      />
    </template>
  </Dialog>
</template>
<script setup lang="ts">
  import * as yup from 'yup'
  import Footer from '@/shared/components/Footer.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import EditRecordButton from '@/shared/components/EditRecordButton.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import useCompany from '@/store/company'
  import useNotifications from '@/store/notification'
  import { storeToRefs } from 'pinia'
  import { useField } from 'vee-validate'
  import { computed, onMounted, reactive, ref } from 'vue'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import router from '@/router'
  import ExternalLetterMapped from '../../models/ExternalLetterMapped'
  import {
    updateExternalLetters,
    uploadExternalLettersZip,
  } from '../../services/ExternalLetterService'
  import { ExternalLetterInputDto } from '../../services/dtos/ICreateExternalLetters'
  import { useRoute } from 'vue-router'
  import { getLetterConfigById } from '../../services/LetterConfgServices'
  import { getLetterRequestsByConfigId } from '../../services/LetterRequestService'
  import Column from 'primevue/column'
  import { getFileUrlPresigned } from '@/shared/services/fileService'
  import { useConfirm } from 'primevue/useconfirm'
  import IUpdateExternalLetter from '../../services/dtos/IUpdateExternalLetter'
  import { FilterMatchMode } from 'primevue/api'

  const path = useRoute()
  const letterCertifConfigId = ref()
  const confirm = useConfirm()
  const companyStore = useCompany()
  const notificationStore = useNotifications()
  const { company } = storeToRefs(companyStore)
  const isEditMode = ref(false)
  const modalVisible = ref(false)
  const isLoading = ref(false)
  const saving = ref(false)

  const requestsToDelete = ref<string[]>([])
  const requestsToCreate = ref<ExternalLetterMapped[]>([])
  const filesAreLoadig = ref(false)
  const file = ref<File>()
  const selectedRecordsToRemove = ref<ExternalLetterMapped[]>([])

  const mappedExternalFiles = reactive<{
    data: ExternalLetterMapped[]
    totalItems: number
  }>({
    data: [],
    totalItems: 0,
  })

  const lettersRequests = reactive<{
    data: ExternalLetterMapped[]
    totalItems: number
  }>({
    data: [],
    totalItems: 0,
  })

  const recordsIds = computed(() => {
    return [
      letterCertifConfigId.value,
      ...lettersRequests.data.map((lr) => lr.id),
    ]
  })

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
  })

  const onCloseDialog = () => {
    modalVisible.value = false
    mappedExternalFiles.data = []
  }

  const downloadFile = (urlOrKey: string) => {
    if (!urlOrKey.startsWith('http')) {
      getFileUrlPresigned(urlOrKey).then((url) => {
        window.open(url, '_blank')
      })
    } else {
      window.open(urlOrKey, '_blank')
    }
  }

  onMounted(async () => {
    isLoading.value = true
    const { id } = path.params
    letterCertifConfigId.value = String(id)
    if (letterCertifConfigId.value) {
      const letterConfig = await getLetterConfigById(letterCertifConfigId.value)
      name.value = letterConfig.name
      description.value = letterConfig.description || ''
      emailsToNotify.value = letterConfig.emailsToNotify || []
      companyStore.setCompany({
        id: letterConfig.company.id,
        companyId: letterConfig.company.companyId,
        code: letterConfig.company.code,
        name: letterConfig.company.name,
        client: {
          id: letterConfig.company.client.id,
          code: letterConfig.company.client.code,
          name: letterConfig.company.client.name,
        },
      })

      const response = await getLetterRequestsByConfigId(letterConfig.id)
      lettersRequests.data = response.data.map((lr: any) => {
        return {
          id: lr.id || '',
          fileName: lr.employee,
          size: lr.size || '',
          employee: {
            code: lr.employee.code || '',
            id: lr.employee.id || '',
            name: lr.employee.name || '',
          },
          url: lr.urlFile || '',
          error: '',
        }
      })

      lettersRequests.totalItems = response.totalItems
    }
    isLoading.value = false
  })

  const getExternalLettersToCreate = computed(() => {
    const externalLetters: ExternalLetterInputDto[] = []
    lettersRequests.data.forEach((exFile) => {
      if (exFile.employee && exFile.url && exFile.id === undefined) {
        const externalLetter: ExternalLetterInputDto = {
          employeeId: exFile.employee.id,
          url: exFile.url,
        }
        externalLetters.push(externalLetter)
      }
    })
    return externalLetters
  })

  //add external letters to the main table
  const addExternalLetters = () => {
    modalVisible.value = false
    requestsToCreate.value = mappedExternalFiles.data
    //add to main table
    lettersRequests.data = [...lettersRequests.data, ...requestsToCreate.value]
    mappedExternalFiles.data = []
  }

  //get payrolls ids
  const getPayrollsIds = computed<string[]>(() => {
    const payrollsIds = new Set<string>()
    lettersRequests.data.forEach((file) => {
      if (file.payrollId) payrollsIds.add(file.payrollId)
    })

    return Array.from(payrollsIds)
  })

  const saveFile = async (event: any) => {
    const file = event.files[0]
    file.value = file

    if (company.value === null) {
      notificationStore.addErrorNotification({
        detail: 'No se ha seleccionado una empresa',
        severity: 'info',
        summary: 'Favor selecciona un empresa antes de cargar los archivos',
      })
    } else {
      filesAreLoadig.value = true
      const response = await uploadExternalLettersZip(file, company.value.id)
      //validate if already exist a file associated to the employe
      let files = response.data
      files = files.map((file: ExternalLetterMapped) => {
        const exist = lettersRequests.data.find(
          (exFile: ExternalLetterMapped) =>
            exFile.employee?.id === file.employee?.id
        )
        if (exist) {
          file.error = 'Ya existe un archivo asociado a este empleado'
        }

        return file
      })

      mappedExternalFiles.data = response.data
      mappedExternalFiles.totalItems = response.totalItems
      filesAreLoadig.value = false
    }
  }

  const removeFile = () => {
    file.value = undefined
  }

  const nameSchema = yup.string().required('El nombre es requerido')
  const descriptionSchema = yup.string().required('La descripción es requerida')
  const emailsSchema = yup
    .array()
    .test('emails', 'Ingresa un correo valido', (value) => {
      if (value) {
        const emails = value
        const regex = new RegExp(
          '^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
        )
        return emails.every((email) => regex.test(email.trim()))
      }
      return true
    })

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${lettersRequests.totalItems || 0}`
  })

  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: name,
    errorMessage: nameError,
    validate: nameValidate,
  } = useField<string>('description', nameSchema)
  const {
    value: emailsToNotify,
    errorMessage: emailsError,
    validate: emailsValidate,
  } = useField<string[]>('emailsToNotify', emailsSchema)

  const isErrorInMappedFiles = computed(() => {
    return mappedExternalFiles.data.some((file) => file.error)
  })

  const isValidToSave = computed(() => {
    return (
      lettersRequests.data.length > 0 &&
      !isErrorInMappedFiles.value &&
      name.value &&
      description.value &&
      !emailsError.value &&
      isEditMode.value
    )
  })

  const onSave = async () => {
    if (
      !isValidToSave.value ||
      path.params.id === undefined ||
      path.params.id === ''
    ) {
      return
    }

    const dto: IUpdateExternalLetter = {
      letterConfigId: path.params.id as string,
      name: name.value,
      description: description.value,
      emailsToNotify: emailsToNotify.value,
      companyId: company.value?.id || '',
      payrollsIds: getPayrollsIds.value,
      externalLetters: getExternalLettersToCreate.value,
      lettersToDelete: requestsToDelete.value,
    }

    saving.value = true
    const response = await updateExternalLetters(dto)
    saving.value = false
    if (response === 'OK') {
      notificationStore.addSuccesNotification({
        detail: 'Cartas actualizadas con exito',
        severity: 'success',
        summary: 'Cartas externas actualizadas',
      })
      router.back()
    }
  }

  const isValidToAdd = computed(() => {
    return mappedExternalFiles.data.length > 0 && !isErrorInMappedFiles.value
  })

  const deleteLetter = (event: any, id: string) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Realmente quieres eliminar este registro?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'si',
      rejectLabel: 'no',
      acceptIcon: 'pi pi-check',
      accept: async () => {
        if (id) {
          const letterDelete = lettersRequests.data.find(
            (lr: any) => lr.id === id
          )
          lettersRequests.data = lettersRequests.data.filter(
            (lr: any) => lr.id !== id
          )
          requestsToDelete.value.push(id)
        }
      },
      reject: () => {},
    })
  }

  const onRemoveRecords = (event: any) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Realmente quieres eliminar los registros?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'si',
      rejectLabel: 'no',
      acceptIcon: 'pi pi-check',
      accept: async () => {
        if (selectedRecordsToRemove.value.length === 0) {
          notificationStore.addErrorNotification({
            detail: 'No hay registros seleccionados',
            severity: 'info',
            summary: 'Favor selecciona un registro para eliminar',
          })
          return
        }
        mappedExternalFiles.data = mappedExternalFiles.data.filter(
          (file) => !selectedRecordsToRemove.value.includes(file)
        )
        mappedExternalFiles.totalItems = mappedExternalFiles.data.length
        selectedRecordsToRemove.value = []
      },
      reject: () => {},
    })
  }

  const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })

  const mainFilters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })

  const toggleEditMode = (value: boolean) => {
    isEditMode.value = value
  }
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 100%;
  }

  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
